import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store'; // Adjust the import path as needed
import { smsSettingsEndpoints } from '../../services/api/endpoints/sms-settings.endpoints';

interface SmsSettings {
  isSMSEnabled: boolean;
  isScheduleSMSEnabled: boolean;
  isRescheduleSMSEnabled: boolean;
  isCancelSMSEnabled: boolean;
  isReminderSMSEnabled: boolean;
  scheduledSMSCount: number;
  verificationSMSCount: number;
  rescheduledSMSCount: number;
  canceledSMSCount: number;
  reminderSMSCount: number;
  scheduleSMSFormat: string;
  rescheduleSMSFormat: string;
  cancelSMSFormat: string;
  reminderSMSFormat: string;
}

interface SmsSettingsState {
  smsSettings: SmsSettings | null; // Single object for SMS settings
  isLoading: boolean;
  error: string | null;
}

const initialState: SmsSettingsState = {
  smsSettings: null, // Initialize as null
  isLoading: false,
  error: null,
};

// Async Thunk to fetch SMS settings
export const fetchSmsSettings = createAsyncThunk(
  'smsSettings/fetchSmsSettings',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await smsSettingsEndpoints.getSmsSettings(organizationId);
      return response; // Return the fetched SMS settings
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch SMS settings');
    }
  }
);

// Async Thunk to create or update SMS settings
export const saveSmsSettings = createAsyncThunk(
  'smsSettings/saveSmsSettings',
  async (
    { organizationId, smsSettings }: { organizationId: string; smsSettings: SmsSettings },
    { rejectWithValue }
  ) => {
    try {
      const response = await smsSettingsEndpoints.createSmsSettings(organizationId, smsSettings);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to save SMS settings');
    }
  }
);

export const updateSmsSettings = createAsyncThunk(
  'smsSettings/updateSmsSettings',
  async (
    { organizationId, smsSettings }: { organizationId: string; smsSettings: SmsSettings },
    { rejectWithValue }
  ) => {
    try {
      const response = await smsSettingsEndpoints.updateSmsSettings(organizationId, smsSettings);
      return response; // Return the updated SMS settings
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update SMS settings');
    }
  }
);

// Async Thunk to delete SMS settings
export const deleteSmsSettings = createAsyncThunk(
  'smsSettings/deleteSmsSettings',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      await smsSettingsEndpoints.deleteSmsSettings(organizationId);
      return null; // Return null to indicate deletion
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete SMS settings');
    }
  }
);

const smsSettingsSlice = createSlice({
  name: 'smsSettings',
  initialState,
  reducers: {
    clearSmsSettings(state) {
      state.smsSettings = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch SMS settings
      .addCase(fetchSmsSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSmsSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.smsSettings = action.payload; // Store the fetched SMS settings
      })
      .addCase(fetchSmsSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Save SMS settings (create or update)
      .addCase(saveSmsSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveSmsSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.smsSettings = action.payload; // Store the saved SMS settings
      })
      .addCase(saveSmsSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update SMS settings
      .addCase(updateSmsSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSmsSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.smsSettings = action.payload; // Store the updated SMS settings
      })
      .addCase(updateSmsSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete SMS settings
      .addCase(deleteSmsSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSmsSettings.fulfilled, state => {
        state.isLoading = false;
        state.smsSettings = null; // Clear SMS settings after deletion
      })
      .addCase(deleteSmsSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSmsSettings } = smsSettingsSlice.actions;
export const selectSmsSettings = (state: RootState) => state.smsSettings;
export default smsSettingsSlice.reducer;
